/**
 * Unified subscription management service
 */

import type { 
  SubscriptionType, 
  UnifiedSubscriptionStatus, 
  LicenseValidationResult,
  LicenseActivationResult,
  LicenseInfo,
  DeviceInfo
} from '$lib/types/subscription';
import { 
  SUBSCRIPTION_LIMITS, 
  FEATURE_ACCESS,
  STORAGE_KEYS,
  LICENSE_CONFIG 
} from '$lib/constants/subscription';
import { ServerTrialService as TrialService } from '../ai/ServerTrialService';
import { usageTracker } from '../UsageTrackingService';
import { getCurrentUsage } from '$lib/stores/usageStore';
import { usageStore } from '$lib/stores/usageStore';
import { get } from 'svelte/store';
import { LicenseServiceFactory } from '../license/LicenseServiceFactory';
import type { LicenseServiceSuite } from '../license/LicenseServiceFactory';

export class SubscriptionService {
  private static instance: SubscriptionService;
  private trialService: TrialService;
  private licenseServices: LicenseServiceSuite;
  private cachedStatus: UnifiedSubscriptionStatus | null = null;
  private statusListeners: Set<(status: UnifiedSubscriptionStatus) => void> = new Set();
  private rateLimitCache: { grammar?: any; ai?: any; timestamp?: number } = {};

  private constructor() {
    this.trialService = TrialService.getInstance();
    this.licenseServices = LicenseServiceFactory.createLicenseServices();
    
    // Try to load cached premium status immediately
    this.cachedStatus = this.getCachedPremiumStatus();
    if (this.cachedStatus) {
      // Notify any early subscribers immediately
      setTimeout(() => this.notifyListeners(), 0);
    }
    
    // Initialize subscription on first load
    this.initializeSubscription();
  }

  static getInstance(): SubscriptionService {
    if (!SubscriptionService.instance) {
      SubscriptionService.instance = new SubscriptionService();
    }
    return SubscriptionService.instance;
  }

  /**
   * Initialize subscription system
   * Optimized to minimize delays and prioritize cached data
   */
  private async initializeSubscription(): Promise<void> {
    try {
      // If we already have a cached premium status, validate it in background
      // but don't clear it immediately
      const hasCachedPremium = this.cachedStatus?.type === 'premium';

      // Reduced initial delay - only wait for essential services
      // Background validation will happen async
      await new Promise(resolve => setTimeout(resolve, 500));

      // Check for existing premium licenses using new system
      // This is local storage check, very fast
      const hasValidLicense = await this.checkValidLicenses();

      if (!hasValidLicense) {
        // Only clear cached status if we confirmed no valid license
        if (hasCachedPremium) {
          this.cachedStatus = null; // Clear invalid premium status
        }

        // Initialize trial if first time user
        if (!this.trialService.hasHadTrial()) {
          await this.trialService.initializeTrial();
        }
      }

      // Update status immediately (now uses local data primarily)
      await this.updateStatus();

      // Additional server validation happens in background via syncUsageFromServer
    } catch (error) {
      console.error('Failed to initialize subscription system:', error);
      // If we have cached premium, keep it during errors
      if (!this.cachedStatus?.type) {
        // Only use fallback if no cached status
        this.updateLocalStatus();
      }
    }
  }

  /**
   * Check for valid licenses using new license validation system
   */
  private async checkValidLicenses(): Promise<boolean> {
    try {
      // Check licenses in secure storage
      const licenseKeys = await this.licenseServices.storage.listLicenseKeys();
      // Found licenses in storage
      
      for (const licenseKey of licenseKeys) {
        const result = await this.licenseServices.validator.validateLicense(licenseKey);
        if (result.isValid) {
          // License is valid
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Failed to check valid licenses:', error);
      return false;
    }
  }

  /**
   * Get unified subscription status
   */
  getStatus(): UnifiedSubscriptionStatus {
    if (!this.cachedStatus) {
      // Check local storage for cached premium status
      const cachedPremiumStatus = this.getCachedPremiumStatus();

      if (cachedPremiumStatus) {
        // Use cached premium status while validating
        this.cachedStatus = cachedPremiumStatus;
        // Trigger async update to validate, but with delay to avoid overwriting immediately
        setTimeout(() => this.updateStatus(), 3000);
        return cachedPremiumStatus;
      }

      // CRITICAL FIX: Check if trial is active synchronously before defaulting to FREE
      // This prevents the race condition where trial users see FREE tier limits
      const isTrialActive = this.trialService.isActive();
      const trialInfo = isTrialActive ? this.trialService.getTrialInfo() : undefined;
      const tier = isTrialActive ? 'trial' : 'free';
      const limits = isTrialActive ? SUBSCRIPTION_LIMITS.TRIAL : SUBSCRIPTION_LIMITS.FREE;
      const features = isTrialActive ? FEATURE_ACCESS.TRIAL : FEATURE_ACCESS.FREE;

      // Return appropriate status based on trial check while still loading full status
      const defaultStatus: UnifiedSubscriptionStatus = {
        type: tier,
        isActive: isTrialActive,
        features,
        dailySpellCheckLimit: limits.dailySpellCheckLimit,
        dailyAIAnalysisLimit: limits.dailyAIAnalysisLimit,
        maxTextLength: limits.maxTextLength,
        canUseUnlimited: limits.canUseUnlimited,
        currentDailyUsage: {
          spellChecks: 0,
          aiAnalyses: 0
        },
        trial: trialInfo || undefined
      };

      // Trigger async update
      this.updateStatus();

      return defaultStatus;
    }
    return this.cachedStatus;
  }

  /**
   * Get current subscription (alias for getStatus for backward compatibility)
   */
  getSubscription(): UnifiedSubscriptionStatus {
    return this.getStatus();
  }

  /**
   * Public method to force update subscription status
   */
  async updateSubscriptionStatus(): Promise<void> {
    // Clear cached status to force recalculation
    this.cachedStatus = null;
    await this.updateStatus();
  }

  /**
   * Update subscription status using only local data (no API calls)
   */
  private updateLocalStatus(): void {
    const type = 'free'; // Default to free when offline
    const limits = SUBSCRIPTION_LIMITS.FREE;
    const features = FEATURE_ACCESS.FREE;
    
    // Use local usage data only
    const localUsage = getCurrentUsage();
    let spellChecks = 0;
    let aiAnalyses = 0;
    
    const today = new Date().toISOString().split('T')[0];
    const todayUsage = localUsage.dailyUsage[today];
    
    if (todayUsage) {
      if (todayUsage.models['grammar-check']) {
        spellChecks = todayUsage.models['grammar-check'].totalRequests || 0;
      }
      
      Object.values(todayUsage.models).forEach((modelStats: any) => {
        if (modelStats.provider !== 'local' && modelStats.model !== 'grammar-check') {
          aiAnalyses += modelStats.totalRequests || 0;
        }
      });
    }
    
    this.cachedStatus = {
      type,
      isActive: false,
      dailySpellCheckLimit: limits.dailySpellCheckLimit,
      dailyAIAnalysisLimit: limits.dailyAIAnalysisLimit,
      maxTextLength: limits.maxTextLength,
      canUseUnlimited: limits.canUseUnlimited,
      currentDailyUsage: {
        spellChecks,
        aiAnalyses
      },
      features,
      trial: undefined
    };
    
    // Store in localStorage
    localStorage.setItem(STORAGE_KEYS.SUBSCRIPTION_STATUS, JSON.stringify(this.cachedStatus));
  }

  /**
   * Update subscription status
   */
  async updateStatus(): Promise<void> {
    const previousType = this.cachedStatus?.type;
    const wasCachedPremium = previousType === 'premium';
    const type = await this.determineSubscriptionType();
    
    // If we had cached premium but validation says it's not valid,
    // only update if we're certain (not just a network error)
    if (wasCachedPremium && type !== 'premium') {
      // Double-check before downgrading from premium
      const recheckLicense = await this.checkValidLicenses();
      if (recheckLicense) {
        // License is actually valid, keep premium
        return; // Don't update status
      }
    }
    const limits = SUBSCRIPTION_LIMITS[type.toUpperCase() as keyof typeof SUBSCRIPTION_LIMITS];
    const features = FEATURE_ACCESS[type.toUpperCase() as keyof typeof FEATURE_ACCESS];
    
    // Force reload trial info from localStorage
    const trialInfo = this.trialService.getTrialInfo();
    
    // Check if trial just expired (transition from trial to free)
    if (previousType === 'trial' && type === 'free' && trialInfo?.hasExpired) {
      this.checkAndShowTrialExpiredModal();
    }
    
    // Get current usage - prioritize local data, sync from server in background
    let spellChecks = 0;
    let aiAnalyses = 0;
    let monthlySpellChecks = 0;
    let monthlyAiAnalyses = 0;

    // Always start with local data for immediate response
    const localUsage = get(usageStore);
    const today = new Date().toISOString().split('T')[0];
    const todayUsage = localUsage.dailyUsage[today];

    if (todayUsage) {
      if (todayUsage.models && todayUsage.models['grammar-check']) {
        spellChecks = todayUsage.models['grammar-check'].totalRequests || 0;
      }

      if (todayUsage.models) {
        Object.values(todayUsage.models).forEach(modelStats => {
          if (modelStats.provider !== 'local' && modelStats.model !== 'grammar-check') {
            aiAnalyses += modelStats.totalRequests || 0;
          }
        });
      }
    }

    // Estimate monthly from daily (since server monthly data is usually same as daily anyway)
    monthlySpellChecks = spellChecks * 30;
    monthlyAiAnalyses = aiAnalyses * 30;

    // Try to get server data in background (with parallel calls and caching)
    // Don't block the UI for server data - local is sufficient
    this.syncUsageFromServer().catch(err => {
      console.debug('Background usage sync failed (non-blocking):', err);
    });

    this.cachedStatus = {
      type,
      isActive: type !== 'free',
      
      // Limits
      dailySpellCheckLimit: limits.dailySpellCheckLimit,
      dailyAIAnalysisLimit: limits.dailyAIAnalysisLimit,
      maxTextLength: limits.maxTextLength,
      canUseUnlimited: limits.canUseUnlimited,
      
      // Current usage
      currentDailyUsage: {
        spellChecks,
        aiAnalyses
      },
      
      // Monthly usage (new field)
      currentMonthlyUsage: {
        spellChecks: monthlySpellChecks,
        aiAnalyses: monthlyAiAnalyses
      },
      
      // Trial info
      trial: trialInfo || undefined,
      
      // Features
      features
    };

    // Notify listeners
    this.notifyListeners();
  }

  /**
   * Determine current subscription type
   */
  private async determineSubscriptionType(): Promise<SubscriptionType> {
    try {
      // Check for beta license first (before premium check)
      const betaLicense = await this.checkBetaLicense();
      if (betaLicense) {
        return 'beta';
      }

      // Check for premium license using new system
      const hasValidLicense = await this.hasValidLicenseNew();
      if (hasValidLicense) {
        // Valid license detected - premium tier
        return 'premium';
      }

      // Check for active trial
      if (this.trialService.isActive()) {
        return 'trial';
      }

      // Default to free
      return 'free';
    } catch (error) {
      console.error('Failed to determine subscription type:', error);
      // Fallback to old logic
      return this.determineSubscriptionTypeFallback();
    }
  }

  /**
   * Check if user has a beta license
   */
  private async checkBetaLicense(): Promise<boolean> {
    try {
      const licenseKeys = await this.licenseServices.storage.listLicenseKeys();

      for (const licenseKey of licenseKeys) {
        // Check if it's a beta key by prefix
        if (licenseKey.toUpperCase().startsWith('BETA-')) {
          const result = await this.licenseServices.validator.validateLicense(licenseKey);
          // Check both metadata.type and isBeta flag
          if (result.isValid && (
            result.license?.metadata?.type === 'beta' ||
            (result.license?.metadata as any)?.isBeta === true
          )) {
            return true;
          }
        }
      }

      return false;
    } catch (error) {
      console.error('Failed to check beta license:', error);
      return false;
    }
  }

  /**
   * New license validation method
   */
  private async hasValidLicenseNew(): Promise<boolean> {
    return await this.checkValidLicenses();
  }

  /**
   * Fallback subscription type determination
   */
  private determineSubscriptionTypeFallback(): SubscriptionType {
    // Check for active trial
    if (this.trialService.isActive()) {
      return 'trial';
    }

    // Default to free
    return 'free';
  }


  /**
   * Validate license with new validation system
   */
  async validateLicense(licenseKey: string): Promise<LicenseValidationResult> {
    try {
      return await this.licenseServices.validator.validateLicense(licenseKey);
    } catch (error) {
      console.error('License validation failed:', error);
      return {
        isValid: false,
        error: {
          type: 'validation_failed',
          message: error instanceof Error ? error.message : 'Validation failed',
          retryable: true,
          details: {}
        },
        validatedAt: new Date()
      };
    }
  }

  /**
   * Activate premium with license key using new activation flow
   */
  async activateLicense(
    licenseKey: string, 
    options?: { 
      forceActivation?: boolean; 
      progressCallback?: (progress: { stage: string; message: string; progress: number }) => void 
    }
  ): Promise<LicenseActivationResult> {
    try {
      const result = await this.licenseServices.activationFlow.activateLicense(
        licenseKey,
        options
      );
      
      if (result.success) {
        // License is already stored in secure storage by activationFlow
        
        
        // Force update subscription status
        await this.updateStatus();
        
        // Double-check the status was updated
        const newType = await this.determineSubscriptionType();

        // If still not premium or beta, force it to premium
        // (beta licenses should be detected by determineSubscriptionType)
        if (newType !== 'premium' && newType !== 'beta') {
          console.warn('Status not updated to premium/beta, forcing update...');
          this.cachedStatus = {
            ...this.cachedStatus!,
            type: 'premium',
            isActive: true
          };
          this.notifyListeners();
        }
      }
      
      return result;
    } catch (error) {
      console.error('License activation failed:', error);
      return {
        success: false,
        error: {
          type: 'validation_failed',
          message: error instanceof Error ? error.message : 'Activation failed',
          retryable: true,
          details: {}
        }
      };
    }
  }

  /**
   * Deactivate license from current device
   */
  async deactivateLicense(licenseKey?: string): Promise<boolean> {
    try {
      // Use provided license key or get first from storage
      const licenseKeys = await this.licenseServices.storage.listLicenseKeys();
      const targetLicenseKey = licenseKey || licenseKeys[0];
      if (!targetLicenseKey) {
        return false;
      }

      const result = await this.licenseServices.validator.deactivateLicense(targetLicenseKey);
      
      if (result) {
        // License is already removed from secure storage by validator
        
        // Update subscription status
        await this.updateStatus();
      }
      
      return result;
    } catch (error) {
      console.error('License deactivation failed:', error);
      return false;
    }
  }

  /**
   * Get license information for current active license
   */
  async getCurrentLicenseInfo(): Promise<LicenseInfo | null> {
    try {
      const licenseKeys = await this.licenseServices.storage.listLicenseKeys();
      
      for (const licenseKey of licenseKeys) {
        const result = await this.licenseServices.validator.validateLicense(licenseKey);
        if (result.isValid && result.license) {
          return result.license;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Failed to get current license info:', error);
      return null;
    }
  }

  /**
   * Get device information for current license
   */
  async getLicenseDeviceInfo(): Promise<DeviceInfo[]> {
    try {
      const licenseInfo = await this.getCurrentLicenseInfo();
      if (licenseInfo) {
        return await this.licenseServices.deviceManager.getActiveDevices(licenseInfo.licenseKey);
      }
      return [];
    } catch (error) {
      console.error('Failed to get license device info:', error);
      return [];
    }
  }

  /**
   * Check if user can perform action (checks both subscription and server limits)
   * Uses cached rate limit data to minimize server calls
   */
  async canPerformAction(action: 'spellCheck' | 'aiAnalysis'): Promise<boolean> {
    const status = this.getStatus();

    // Check if status is valid
    if (!status || !status.features) {
      // Default to free tier permissions
      return action === 'spellCheck';
    }

    // First check subscription features
    if (action === 'spellCheck') {
      if (!status.features.spellCheck) return false;

      // For unlimited plans, always allow (check before any comparisons)
      if (status.dailySpellCheckLimit === -1) return true;

      // Check local count first for quick response (only if not unlimited)
      if (status.dailySpellCheckLimit !== -1 && status.currentDailyUsage.spellChecks >= status.dailySpellCheckLimit) {
        return false;
      }

      // Check server rate limits with caching (UsageTrackingService has 60s cache)
      try {
        const rateLimit = await usageTracker.checkRateLimit('grammar');
        // If server explicitly says not allowed, respect that
        if (rateLimit.allowed === false) {
          return false;
        }
        return rateLimit.allowed;
      } catch (error) {
        console.warn('Rate limit check failed, using local data only:', error);
        // Fallback to local check only on error
        return status.currentDailyUsage.spellChecks < status.dailySpellCheckLimit;
      }
    }

    if (action === 'aiAnalysis') {
      if (!status?.features?.aiAnalysis) return false;

      // For unlimited plans, always allow (check before any comparisons)
      if (status.dailyAIAnalysisLimit === -1) return true;

      // Check local count first (only if not unlimited)
      if (status.dailyAIAnalysisLimit !== -1 && status.currentDailyUsage.aiAnalyses >= status.dailyAIAnalysisLimit) {
        return false;
      }

      // Check server rate limits with caching
      try {
        const rateLimit = await usageTracker.checkRateLimit('ai');
        // If server explicitly says not allowed, respect that
        if (rateLimit.allowed === false) {
          return false;
        }
        return rateLimit.allowed;
      } catch (error) {
        console.warn('Rate limit check failed, using local data only:', error);
        // Fallback to local check only on error
        return status.currentDailyUsage.aiAnalyses < status.dailyAIAnalysisLimit;
      }
    }

    return false;
  }

  /**
   * Check if text length is within limits
   */
  isWithinTextLimit(textLength: number): boolean {
    const status = this.getStatus();

    // CRITICAL: Double-check trial status if cached status is FREE
    // This prevents race condition where cached status is set to FREE before trial is initialized
    if (status.type === 'free' && this.trialService.isActive()) {
      // Trial is actually active but cached status is wrong
      return textLength <= SUBSCRIPTION_LIMITS.TRIAL.maxTextLength;
    }

    return textLength <= status.maxTextLength;
  }

  /**
   * Record usage (handled by UsageTrackingService, just update status)
   */
  async recordUsage(type: 'spellCheck' | 'aiAnalysis'): Promise<void> {
    // Usage is already tracked by UsageTrackingService
    // Just update our cached status to get latest usage from server
    await this.updateStatus();
  }

  /**
   * Get remaining uses
   */
  getRemainingUses(type: 'spellCheck' | 'aiAnalysis'): number {
    const status = this.getStatus();
    
    if (type === 'spellCheck') {
      if (status.dailySpellCheckLimit === -1) return -1; // Unlimited
      return Math.max(0, status.dailySpellCheckLimit - status.currentDailyUsage.spellChecks);
    }
    
    if (status.dailyAIAnalysisLimit === -1) return -1; // Unlimited
    return Math.max(0, status.dailyAIAnalysisLimit - status.currentDailyUsage.aiAnalyses);
  }

  /**
   * Subscribe to status changes
   */
  onStatusChange(callback: (status: UnifiedSubscriptionStatus) => void): () => void {
    this.statusListeners.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.statusListeners.delete(callback);
    };
  }

  /**
   * Notify listeners of status change
   */
  private notifyListeners(): void {
    if (!this.cachedStatus) return;
    
    this.statusListeners.forEach(listener => {
      listener(this.cachedStatus!);
    });
  }


  /**
   * Get last validation time
   */
  private getLastValidation(): Date | null {
    if (typeof localStorage === 'undefined') return null;
    
    const stored = localStorage.getItem(STORAGE_KEYS.LAST_VALIDATION);
    if (!stored) return null;
    
    return new Date(stored);
  }

  /**
   * Set last validation time
   */
  private setLastValidation(date: Date): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.LAST_VALIDATION, date.toISOString());
  }

  /**
   * Reset subscription (for testing)
   */
  async resetSubscription(): Promise<void> {
    if (typeof localStorage === 'undefined') return;
    
    // Clear all subscription data
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Clear new license storage
    try {
      await this.licenseServices.storage.clearAllLicenses();
    } catch (error) {
      console.error('Failed to clear license storage:', error);
    }
    
    // Reset services
    this.trialService.resetTrial();
    
    // Reset server usage limits (dev mode)
    await usageTracker.resetUsageLimits();
    
    // Clear rate limit cache
    usageTracker.clearRateLimitCache();
    
    // Update status
    this.cachedStatus = null;
    await this.updateStatus();
  }

  /**
   * Format subscription status for display
   */
  formatStatus(): string {
    const status = this.getStatus();
    
    switch (status.type) {
      case 'premium':
        return '프리미엄';
      case 'trial':
        return `무료 체험 (${status.trial?.daysRemaining}일 남음)`;
      case 'free':
        return '무료';
      default:
        return '알 수 없음';
    }
  }

  /**
   * Check and show trial expired modal
   */
  private checkAndShowTrialExpiredModal(): void {
    const TRIAL_EXPIRED_SHOWN_KEY = 'trial_expired_modal_shown';
    
    // Check if we've already shown the modal
    const hasShownModal = localStorage.getItem(TRIAL_EXPIRED_SHOWN_KEY);
    
    if (!hasShownModal) {
      // Show the modal after a short delay to ensure UI is ready
      setTimeout(async () => {
        const { showUpgradePrompt, upgradePromptContext } = await import('$lib/stores/subscription');
        
        // Set the context for trial expired
        upgradePromptContext.set({
          reason: 'trial_expired',
          details: undefined
        });
        
        // Show the modal
        showUpgradePrompt.set(true);
        
        // Mark as shown
        localStorage.setItem(TRIAL_EXPIRED_SHOWN_KEY, 'true');
      }, 1000);
    }
  }

  /**
   * Sync usage data from server in background (non-blocking)
   * Uses parallel calls and caching from UsageTrackingService
   */
  private async syncUsageFromServer(): Promise<void> {
    try {
      const { usageTracker } = await import('$lib/services/UsageTrackingService');

      // Parallel fetch with caching (UsageTrackingService has 60s cache)
      const [serverSummary, monthlySummary] = await Promise.all([
        usageTracker.getServerSummary('today'),
        usageTracker.getServerSummary('month')
      ]);

      // Update cached status if we got new data
      if (serverSummary && this.cachedStatus) {
        let spellChecks = 0;
        let aiAnalyses = 0;

        if (serverSummary.summary) {
          spellChecks = serverSummary.summary.grammar?.count || 0;
          aiAnalyses = serverSummary.summary.ai?.count || 0;
        }

        // Only update if different from current
        if (spellChecks !== this.cachedStatus.currentDailyUsage.spellChecks ||
            aiAnalyses !== this.cachedStatus.currentDailyUsage.aiAnalyses) {
          this.cachedStatus.currentDailyUsage = {
            spellChecks,
            aiAnalyses
          };

          if (monthlySummary?.summary) {
            this.cachedStatus.currentMonthlyUsage = {
              spellChecks: monthlySummary.summary.grammar?.count || 0,
              aiAnalyses: monthlySummary.summary.ai?.count || 0
            };
          }

          this.notifyListeners();
        }
      }
    } catch (error) {
      // Non-blocking error - just log it
      console.debug('Background usage sync failed:', error);
    }
  }

  /**
   * Get upgrade benefits for current tier
   */
  getUpgradeBenefits(): string[] {
    const status = this.getStatus();
    
    if (status.type === 'premium') {
      return [];
    }
    
    const benefits = [
      '무제한 맞춤법 검사',
      `최대 ${SUBSCRIPTION_LIMITS.PREMIUM.maxTextLength.toLocaleString()}자 텍스트 분석`,
      'AI 기반 상세 문법 분석',
      '내보내기 기능',
      '사용자 사전 기능'
    ];
    
    if (status.type === 'trial') {
      benefits.unshift('체험 기간 종료 후에도 계속 이용');
    }
    
    return benefits;
  }
  
  /**
   * Get cached premium status from local storage
   */
  private getCachedPremiumStatus(): UnifiedSubscriptionStatus | null {
    try {
      // Check if we have stored licenses using secure storage prefix
      let hasStoredLicense = false;
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('secure_license_')) {
          hasStoredLicense = true;
          break;
        }
      }
      
      if (!hasStoredLicense) {
        return null;
      }
      
      // If we have stored licenses, assume premium until validation completes
      const limits = SUBSCRIPTION_LIMITS.PREMIUM;
      const features = FEATURE_ACCESS.PREMIUM;
      
      // Get cached usage data
      const localUsage = getCurrentUsage();
      let spellChecks = 0;
      let aiAnalyses = 0;
      
      const today = new Date().toISOString().split('T')[0];
      const todayUsage = localUsage.dailyUsage[today];
      
      if (todayUsage) {
        if (todayUsage.models['grammar-check']) {
          spellChecks = todayUsage.models['grammar-check'].totalRequests || 0;
        }
        
        Object.values(todayUsage.models).forEach((modelStats: any) => {
          if (modelStats.model && modelStats.model !== 'grammar-check') {
            aiAnalyses += modelStats.totalRequests || 0;
          }
        });
      }
      
      return {
        type: 'premium',
        isActive: true,
        features,
        dailySpellCheckLimit: limits.dailySpellCheckLimit,
        dailyAIAnalysisLimit: limits.dailyAIAnalysisLimit,
        maxTextLength: limits.maxTextLength,
        canUseUnlimited: limits.canUseUnlimited,
        currentDailyUsage: {
          spellChecks,
          aiAnalyses
        },
        trial: undefined
      };
    } catch (error) {
      console.error('Failed to get cached premium status:', error);
      return null;
    }
  }
}